import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useBoolean,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from 'components';
import { ExpertiseMasterNote, ExpertiseDamageNoteType } from 'types';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { useQuery, useMutation } from 'react-query';
import { Api } from 'api';
import { FormControlFormSelect } from 'core';
import { t, Trans } from '@lingui/macro';
import { AdminSaveExpertiseMasterNoteForm } from '../components';

export const AdminExpertiseMasterNoteManagementPage = () => {
  const toast = useToast();
  const [selectedNoteType, setSelectedNoteType] = useState<number>(ExpertiseDamageNoteType.Bodywork);
  const [focusedNote, setFocusedNote] = useState<ExpertiseMasterNote>();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data: responseData, refetch } = useQuery(
    ['GetPaginatedMasterNotes', pagination.pageIndex, pagination.pageSize, selectedNoteType],
    async () => {
      const response = await Api.Expertise.getPaginatedMasterNotes(
        pagination.pageIndex + 1,
        pagination.pageSize,
        selectedNoteType,
      );
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const deleteMutation = useMutation(
    async (id: number) => {
      await Api.Expertise.deleteMasterNote(id);
    },
    {
      onSuccess: () => {
        toast({
          title: t`Silmek istediğinize emin misiniz?`,
          status: 'success',
          isClosable: true,
        });
        refetch();
      },
      onError: () => {
        toast({
          title: t`Hata`,
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const handleDelete = useCallback(
    (id: number) => {
      // eslint-disable-next-line no-alert
      if (window.confirm(t`Silmek istediğinize emin misiniz?`)) {
        deleteMutation.mutate(id);
      }
    },
    [deleteMutation],
  );

  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const { isOpen: isCreateModalOpen, onClose: onCreateModalClose, onOpen: onCreateModalOpen } = useDisclosure();

  const columns = useMemo<ColumnDef<ExpertiseMasterNote, any>[]>(
    () => [
      {
        header: t`Marka`,
        accessorKey: 'brandName',
      },
      {
        header: t`Model`,
        accessorKey: 'modelName',
      },
      {
        header: t`Yıl`,
        accessorKey: 'year',
        cell: (info) => info.getValue() || t`Tümü`,
      },
      {
        header: t`Not Türü`,
        accessorKey: 'noteType',
        cell: (info) => {
          switch (info.getValue()) {
            case ExpertiseDamageNoteType.Mechanic:
              return t`Mekanik`;
            case ExpertiseDamageNoteType.Bodywork:
            default:
              return t`Kaporta`;
          }
        },
      },
      {
        header: t`Not`,
        accessorKey: 'note',
      },
      {
        id: 'action',
        header: t`Aksiyonlar`,
        cell: ({ row }) => {
          return (
            <HStack gap={2}>
              <IconButton
                icon={<EditIcon />}
                aria-label={t`Düzenle`}
                size="sm"
                onClick={() => {
                  setFocusedNote(row.original);
                  onEditModalOpen();
                }}
              />
              <IconButton
                icon={<DeleteIcon />}
                aria-label={t`Sil`}
                colorScheme="red"
                size="sm"
                onClick={() => handleDelete(row.original.id)}
              />
            </HStack>
          );
        },
      },
    ],
    [handleDelete, onEditModalOpen],
  );

  const [isMutationLoading, { on: onLoading, off: offLoading }] = useBoolean();

  const onSubmitStart = () => {
    onLoading();
  };

  const onSubmitComplete = (data?: ExpertiseMasterNote) => {
    offLoading();
    if (data) {
      onEditModalClose();
      onCreateModalClose();
      refetch();
    }
  };

  const totalCount = responseData?.total || 0;
  const items = responseData?.items || [];

  return (
    <>
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Düzenle</Trans>
            <ModalCloseButton disabled={isMutationLoading} onClick={onEditModalClose} />
          </ModalHeader>
          <ModalBody>
            {isEditModalOpen && (
              <AdminSaveExpertiseMasterNoteForm
                focusedNote={focusedNote}
                formId="editMasterNote"
                onSubmitComplete={onSubmitComplete}
                onSubmitStart={onSubmitStart}
                noteType={selectedNoteType}
              />
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button onClick={onEditModalClose} disabled={isMutationLoading}>
              <Trans>Kapat</Trans>
            </Button>
            <Button colorScheme="blue" type="submit" form="editMasterNote" isLoading={isMutationLoading}>
              <Trans>Kaydet</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isCreateModalOpen} onClose={onCreateModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Oluştur</Trans>
            <ModalCloseButton disabled={isMutationLoading} onClick={onCreateModalClose} />
          </ModalHeader>
          <ModalBody>
            {isCreateModalOpen && (
              <AdminSaveExpertiseMasterNoteForm
                formId="createMasterNote"
                onSubmitComplete={onSubmitComplete}
                onSubmitStart={onSubmitStart}
                noteType={selectedNoteType}
              />
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button onClick={onCreateModalClose} disabled={isMutationLoading}>
              <Trans>İptal</Trans>
            </Button>
            <Button colorScheme="blue" type="submit" form="createMasterNote" isLoading={isMutationLoading}>
              <Trans>Kaydet</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <DataTable
        title={t`Ustadan Ustaya Not Yönetimi`}
        data={items}
        columns={columns}
        usePagination
        tableOptions={{
          state: {
            pagination,
          },
          onPaginationChange: setPagination,
          pageCount: Math.ceil(totalCount / pagination.pageSize),
          manualPagination: true,
          autoResetPageIndex: false,
        }}
        actions={() => {
          return (
            <HStack gap={2}>
              <FormControlFormSelect
                label={t`Not Türü`}
                value={selectedNoteType}
                onChange={(e) => {
                  setSelectedNoteType(parseInt(e.target.value, 10));
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                width="150px"
              >
                <option value={ExpertiseDamageNoteType.Bodywork}>
                  <Trans>Kaporta</Trans>
                </option>
                <option value={ExpertiseDamageNoteType.Mechanic}>
                  <Trans>Mekanik</Trans>
                </option>
              </FormControlFormSelect>
              <IconButton
                icon={<AddIcon />}
                aria-label={t`Ekle`}
                onClick={() => {
                  setFocusedNote(undefined);
                  onCreateModalOpen();
                }}
                alignSelf="end"
              />
            </HStack>
          );
        }}
      />
    </>
  );
};
